/**
 * Outfit Builder — storefront logic
 * ---------------------------------
 * Everything is driven by the JSON config the section prints into
 * <script data-ob-config>. Slots (hat / top / bottom / shoes / sunglasses /
 * socks / ...) are fully data-driven: adding a slot in the theme editor is
 * enough — no changes needed in this file.
 *
 * Public events dispatched on the section element:
 *   outfit-builder:change        detail: { selection }
 *   outfit-builder:items-added   detail: { items }
 */
(function () {
  'use strict';

  var FOCUSABLE = 'button, [href], select, input, [tabindex]:not([tabindex="-1"])';

  // Some stores (and currency-converter apps) set shop.money_format to markup,
  // e.g. `<span class="cvc-money">€{{amount}}</span>`. The total is written with
  // textContent, so strip tags (and decode entities like &euro;) to plain text.
  function stripHtml(str) {
    if (str.indexOf('<') === -1 && str.indexOf('&') === -1) return str;
    var el = document.createElement('div');
    el.innerHTML = str;
    return (el.textContent || el.innerText || '').trim();
  }

  // Bucket a slot's products by category (Shopify product type), preserving the
  // order categories first appear in. Each bucket keeps the products' original
  // indices so the rest of the code can keep addressing products by index.
  function groupByCategory(products) {
    var groups = [];
    var byKey = {};
    products.forEach(function (product, index) {
      var name = (product.category || '').trim();
      var key = name.toLowerCase();
      if (!Object.prototype.hasOwnProperty.call(byKey, key)) {
        byKey[key] = groups.length;
        groups.push({ name: name, indices: [] });
      }
      groups[byKey[key]].indices.push(index);
    });
    return groups;
  }

  function OutfitBuilder(root) {
    this.root = root;

    var cfgEl = root.querySelector('[data-ob-config]');
    try {
      this.config = JSON.parse(cfgEl.textContent);
    } catch (e) {
      console.error('[outfit-builder] invalid config JSON', e);
      return;
    }

    // Ignore slots that have no products (collection not assigned yet).
    this.slots = (this.config.slots || []).filter(function (s) {
      return s.products && s.products.length > 0;
    });

    // Group each slot's products into categories (by product type). A slot that
    // resolves to a single category browses as a flat list, exactly like before.
    var categoriesEnabled = this.config.categoriesEnabled;
    this.slots.forEach(function (slot) {
      slot.categories = groupByCategory(slot.products);
      slot.useCategories = categoriesEnabled && slot.categories.length > 1;
    });

    this.els = {
      canvas: root.querySelector('[data-ob-canvas]'),
      rail: root.querySelector('[data-ob-rail]'),
      picker: root.querySelector('[data-ob-picker]'),
      drawer: root.querySelector('[data-ob-drawer]'),
      overlay: root.querySelector('[data-ob-overlay]'),
      total: root.querySelector('[data-ob-total]'),
      shopBtn: root.querySelector('[data-ob-shop]'),
      resetBtn: root.querySelector('[data-ob-reset]'),
      viewBtns: root.querySelectorAll('[data-ob-view]')
    };

    // selection: slotId -> product index (or null = empty slot)
    // sizes:     slotId -> chosen variant id
    this.state = { view: 'front', selection: {}, sizes: {} };
    this.activeSlotId = null;
    // Picker navigation: 'categories' (choose Hoodies / T-Shirts / …) or
    // 'products' (choose an item, optionally inside the active category).
    this.pickerView = 'products';
    this.activeCategory = null;
    this.lastFocus = null;

    this.applyDefaultSelection();
    this.applySelectionFromUrl();
    this.bind();
    this.renderRail();
    this.renderCanvas();
    this.updateTotal();
    this.fitCanvas();
  }

  /* ------------------------------------------------------------------ */
  /* State                                                               */
  /* ------------------------------------------------------------------ */

  OutfitBuilder.prototype.applyDefaultSelection = function () {
    var sel = this.state.selection;
    this.slots.forEach(function (slot) {
      sel[slot.id] = slot.preselect ? 0 : null;
    });
  };

  // Share links: ?look=top:hoodie-grey,bottom:track-pants-black
  OutfitBuilder.prototype.applySelectionFromUrl = function () {
    var param = new URLSearchParams(window.location.search).get('look');
    if (!param) return;
    var sel = this.state.selection;
    var slots = this.slots;
    param.split(',').forEach(function (pair) {
      var parts = pair.split(':');
      if (parts.length !== 2) return;
      var slot = slots.find(function (s) { return s.id === parts[0]; });
      if (!slot) return;
      if (parts[1] === 'none') {
        if (slot.optional) sel[slot.id] = null;
        return;
      }
      var idx = slot.products.findIndex(function (p) { return p.handle === parts[1]; });
      if (idx >= 0) sel[slot.id] = idx;
    });
  };

  OutfitBuilder.prototype.writeSelectionToUrl = function () {
    var sel = this.state.selection;
    var parts = [];
    this.slots.forEach(function (slot) {
      var idx = sel[slot.id];
      parts.push(slot.id + ':' + (idx === null ? 'none' : slot.products[idx].handle));
    });
    var url = new URL(window.location.href);
    url.searchParams.set('look', parts.join(','));
    window.history.replaceState({}, '', url);
  };

  OutfitBuilder.prototype.selectedProduct = function (slot) {
    var idx = this.state.selection[slot.id];
    return idx === null || idx === undefined ? null : slot.products[idx];
  };

  OutfitBuilder.prototype.setSelection = function (slotId, index) {
    this.state.selection[slotId] = index;
    delete this.state.sizes[slotId];
    this.renderRail();
    this.renderCanvas();
    this.updateTotal();
    this.writeSelectionToUrl();
    this.emit('outfit-builder:change', { selection: this.state.selection });
  };

  // Empty every slot (including any preselected defaults) and clear chosen
  // sizes, returning the builder to a blank canvas.
  OutfitBuilder.prototype.reset = function () {
    var self = this;
    this.slots.forEach(function (slot) { self.state.selection[slot.id] = null; });
    this.state.sizes = {};
    this.renderRail();
    this.renderCanvas();
    this.updateTotal();
    this.writeSelectionToUrl();
    this.emit('outfit-builder:change', { selection: this.state.selection });
  };

  // Reset only makes sense once something is selected, so hide it on a blank
  // outfit to avoid a no-op control.
  OutfitBuilder.prototype.updateResetVisibility = function () {
    if (!this.els.resetBtn) return;
    var self = this;
    var anySelected = this.slots.some(function (slot) {
      return self.selectedProduct(slot) !== null;
    });
    this.els.resetBtn.hidden = !anySelected;
  };

  OutfitBuilder.prototype.cycle = function (slotId, dir) {
    var slot = this.slots.find(function (s) { return s.id === slotId; });
    if (!slot) return;
    // Optional slots include a virtual "none" position at the end of the loop.
    var count = slot.products.length + (slot.optional ? 1 : 0);
    var current = this.state.selection[slotId];
    var pos = current === null ? slot.products.length : current;
    var next = (pos + dir + count) % count;
    this.setSelection(slotId, next === slot.products.length ? null : next);
  };

  OutfitBuilder.prototype.emit = function (name, detail) {
    this.root.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
  };

  /* ------------------------------------------------------------------ */
  /* Canvas                                                              */
  /* ------------------------------------------------------------------ */

  OutfitBuilder.prototype.renderCanvas = function () {
    var self = this;
    var canvas = this.els.canvas;
    canvas.innerHTML = '';

    var layered = this.config.mode === 'layered';
    var anySelected = false;

    // Base figure (model / mannequin) sits beneath every garment layer.
    var base = this.config.base || {};
    var baseSrc = this.state.view === 'back' ? (base.back || base.front) : base.front;
    if (layered && baseSrc) {
      var baseLayer = document.createElement('div');
      baseLayer.className = 'ob-layer ob-layer--full';
      baseLayer.style.zIndex = 0;
      var baseImg = document.createElement('img');
      baseImg.src = baseSrc;
      baseImg.alt = '';
      baseLayer.appendChild(baseImg);
      canvas.appendChild(baseLayer);
      anySelected = true; // canvas is never "empty" when a base figure is shown
    }

    // Draw lower layers first so higher layers paint on top.
    var ordered = this.slots.slice().sort(function (a, b) { return a.layer - b.layer; });

    // First pass: resolve which slots actually render (selected and having an
    // image for the current view). We need the full set before positioning
    // because a slot's horizontal placement can depend on how many of its
    // group are shown.
    var drawList = [];
    ordered.forEach(function (slot) {
      var product = self.selectedProduct(slot);
      if (!product) return;
      var src = self.state.view === 'back' && product.back ? product.back : product.front;
      if (!src) return;
      drawList.push({ slot: slot, product: product, src: src });
    });
    if (drawList.length) anySelected = true;

    // Auto-centering groups (flat-lay only): slots sharing a `group` name are
    // spread evenly across the canvas based on how many are currently shown, so
    // a lone item centers (50%), two sit at 25% / 75%, three at ~16 / 50 / 83.
    // The k-th of n present sits at (k + 0.5) / n. Ungrouped slots keep their
    // configured pos.x; pos.x also sets left-to-right order within a group.
    var groupX = {};
    if (!layered) {
      var groups = {};
      drawList.forEach(function (d) {
        var g = d.slot.group;
        if (!g) return;
        (groups[g] || (groups[g] = [])).push(d.slot);
      });
      Object.keys(groups).forEach(function (g) {
        var members = groups[g].slice().sort(function (a, b) { return a.pos.x - b.pos.x; });
        var n = members.length;
        members.forEach(function (slot, k) {
          groupX[slot.id] = (k + 0.5) / n * 100;
        });
      });
    }

    drawList.forEach(function (d) {
      var slot = d.slot;
      var layer = document.createElement('div');
      layer.className = 'ob-layer' + (layered ? ' ob-layer--full' : '');
      layer.style.zIndex = slot.layer;

      if (!layered) {
        var x = groupX.hasOwnProperty(slot.id) ? groupX[slot.id] : slot.pos.x;
        layer.style.left = x + '%';
        layer.style.top = slot.pos.y + '%';
        layer.style.width = slot.pos.w + '%';
        // Back the flat lay with this slot's colour so a matching photography
        // background melts into it (via the layer's multiply blend).
        if (slot.bg) layer.style.setProperty('--ob-slot-bg', slot.bg);
      }

      var img = document.createElement('img');
      img.src = d.src;
      img.alt = slot.label + ': ' + d.product.title;
      img.loading = 'lazy';
      if (layered) {
        var cal = d.product.cal || {};
        img.style.transform =
          'translate(' + (cal.dx || 0) + '%, ' + (cal.dy || 0) + '%) scale(' + (cal.scale || 1) + ')';
      }

      layer.appendChild(img);
      canvas.appendChild(layer);
    });

    if (!anySelected) {
      var empty = document.createElement('p');
      empty.className = 'ob-canvas-empty';
      empty.textContent = this.config.strings.empty;
      canvas.appendChild(empty);
    }

    this.preloadOtherView();
  };

  // Warm the cache for the opposite view so the flip feels instant.
  OutfitBuilder.prototype.preloadOtherView = function () {
    if (!this.config.backEnabled) return;
    var self = this;
    this.slots.forEach(function (slot) {
      var p = self.selectedProduct(slot);
      if (!p) return;
      var src = self.state.view === 'front' ? p.back : p.front;
      if (src) { var i = new Image(); i.src = src; }
    });
  };

  OutfitBuilder.prototype.setView = function (view) {
    this.state.view = view;
    this.els.viewBtns.forEach(function (btn) {
      var active = btn.dataset.obView === view;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
    });
    this.renderCanvas();
  };

  /* ------------------------------------------------------------------ */
  /* Rail (one card per slot)                                            */
  /* ------------------------------------------------------------------ */

  OutfitBuilder.prototype.renderRail = function () {
    var self = this;
    var rail = this.els.rail;
    rail.innerHTML = '';

    this.slots.forEach(function (slot) {
      var product = self.selectedProduct(slot);

      var card = document.createElement('div');
      card.className = 'ob-slot';
      if (slot.bg) card.style.setProperty('--ob-slot-bg', slot.bg);

      var prev = self.railArrow(slot.id, -1, 'Previous ' + slot.label);
      var next = self.railArrow(slot.id, 1, 'Next ' + slot.label);

      var main = document.createElement('button');
      main.type = 'button';
      main.className = 'ob-slot__main';
      main.setAttribute('aria-haspopup', 'dialog');
      main.addEventListener('click', function () { self.openPicker(slot.id); });

      var thumbWrap = document.createElement('span');
      thumbWrap.className = 'ob-slot__thumb';
      if (product && product.thumb) {
        var img = document.createElement('img');
        img.src = product.thumb;
        img.alt = '';
        img.loading = 'lazy';
        thumbWrap.appendChild(img);
      } else if (slot.icon) {
        // Empty slot: show the collection's image faded as a placeholder, with
        // a "+" badge keeping the "tap to add" affordance.
        thumbWrap.classList.add('ob-slot__thumb--placeholder');
        var icon = document.createElement('img');
        icon.src = slot.icon;
        icon.alt = '';
        icon.loading = 'lazy';
        thumbWrap.appendChild(icon);
        var badge = document.createElement('span');
        badge.className = 'ob-slot__plus';
        badge.textContent = '+';
        thumbWrap.appendChild(badge);
      } else {
        thumbWrap.classList.add('ob-slot__thumb--empty');
        thumbWrap.textContent = '+';
      }

      var text = document.createElement('span');
      text.className = 'ob-slot__text';
      var label = document.createElement('span');
      label.className = 'ob-slot__label';
      label.textContent = slot.label;
      var name = document.createElement('span');
      name.className = 'ob-slot__name';
      name.textContent = product ? product.title : self.config.strings.none;
      text.appendChild(label);
      text.appendChild(name);

      main.appendChild(thumbWrap);
      main.appendChild(text);

      card.appendChild(prev);
      card.appendChild(main);
      card.appendChild(next);
      rail.appendChild(card);
    });

    this.updateResetVisibility();
  };

  OutfitBuilder.prototype.railArrow = function (slotId, dir, label) {
    var self = this;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ob-slot__arrow';
    btn.setAttribute('aria-label', label);
    btn.innerHTML = dir < 0 ? '&lsaquo;' : '&rsaquo;';
    btn.addEventListener('click', function () { self.cycle(slotId, dir); });
    return btn;
  };

  /* ------------------------------------------------------------------ */
  /* Picker panel                                                        */
  /* ------------------------------------------------------------------ */

  // Open the picker for a slot. Slots with more than one category start on the
  // category screen (Hoodies / T-Shirts / Jackets); single-category slots go
  // straight to the flat product list, unchanged from before.
  OutfitBuilder.prototype.openPicker = function (slotId) {
    var slot = this.slots.find(function (s) { return s.id === slotId; });
    if (!slot) return;

    this.activeSlotId = slotId;
    this.activeCategory = null;
    this.pickerView = slot.useCategories ? 'categories' : 'products';

    // Tint the picker's option/category boxes with this slot's colour.
    if (slot.bg) {
      this.els.picker.style.setProperty('--ob-slot-bg', slot.bg);
    } else {
      this.els.picker.style.removeProperty('--ob-slot-bg');
    }

    this.renderPicker();
    this.openPanel(this.els.picker);
  };

  // Render the picker's current screen into the (already open) panel. Keeping
  // the DOM rebuild separate from openPanel lets us swap screens and refresh the
  // selected state without re-opening or stealing focus.
  OutfitBuilder.prototype.renderPicker = function () {
    var self = this;
    var slot = this.slots.find(function (s) { return s.id === self.activeSlotId; });
    if (!slot) return;

    var picker = this.els.picker;
    picker.innerHTML = '';

    if (this.pickerView === 'categories') {
      picker.appendChild(this.panelHeader(slot.label, function () { self.closePanels(); }));
      picker.appendChild(this.categoryGrid(slot));
      return;
    }

    // Product screen. When it was reached through a category, show that
    // category as the title and a back button returning to the category list.
    var inCategory = slot.useCategories && this.activeCategory;
    var title = inCategory ? this.activeCategory.name : slot.label;
    var onBack = inCategory
      ? function () { self.pickerView = 'categories'; self.activeCategory = null; self.renderPicker(); }
      : null;

    picker.appendChild(this.panelHeader(title, function () { self.closePanels(); }, onBack));
    picker.appendChild(this.productGrid(slot));
  };

  // Screen 1: one tile per category.
  OutfitBuilder.prototype.categoryGrid = function (slot) {
    var self = this;
    var grid = document.createElement('div');
    grid.className = 'ob-categories';

    var currentIdx = this.state.selection[slot.id];

    // Optional slots can be cleared straight from the category screen.
    if (slot.optional) {
      grid.appendChild(this.noneTile(currentIdx === null, function () {
        self.setSelection(slot.id, null);
        self.closePanels();
      }));
    }

    slot.categories.forEach(function (category) {
      grid.appendChild(self.categoryTile(slot, category));
    });

    return grid;
  };

  OutfitBuilder.prototype.categoryTile = function (slot, category) {
    var self = this;
    var s = this.config.strings;

    var currentIdx = this.state.selection[slot.id];
    var containsSelection = currentIdx !== null && currentIdx !== undefined &&
      category.indices.indexOf(currentIdx) >= 0;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ob-category' + (containsSelection ? ' is-selected' : '');

    var thumb = document.createElement('span');
    thumb.className = 'ob-category__thumb';
    var thumbSrc = '';
    for (var i = 0; i < category.indices.length; i++) {
      var candidate = slot.products[category.indices[i]];
      if (candidate && candidate.thumb) { thumbSrc = candidate.thumb; break; }
    }
    if (thumbSrc) {
      var img = document.createElement('img');
      img.src = thumbSrc;
      img.alt = '';
      img.loading = 'lazy';
      thumb.appendChild(img);
    } else {
      thumb.classList.add('ob-category__thumb--empty');
    }

    var body = document.createElement('span');
    body.className = 'ob-category__body';
    var name = document.createElement('span');
    name.className = 'ob-category__name';
    name.textContent = category.name || s.other;
    var count = document.createElement('span');
    count.className = 'ob-category__count';
    var n = category.indices.length;
    count.textContent = n + ' ' + (n === 1 ? s.item : s.items);
    body.appendChild(name);
    body.appendChild(count);

    var chevron = document.createElement('span');
    chevron.className = 'ob-category__chevron';
    chevron.setAttribute('aria-hidden', 'true');
    chevron.innerHTML = '&rsaquo;';

    btn.appendChild(thumb);
    btn.appendChild(body);
    btn.appendChild(chevron);

    btn.addEventListener('click', function () {
      self.pickerView = 'products';
      self.activeCategory = category;
      self.renderPicker();
    });

    return btn;
  };

  OutfitBuilder.prototype.noneTile = function (selected, onClick) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ob-category ob-category--none' + (selected ? ' is-selected' : '');
    btn.setAttribute('aria-pressed', String(selected));

    var thumb = document.createElement('span');
    thumb.className = 'ob-category__thumb ob-category__thumb--empty';
    thumb.textContent = '—';

    var body = document.createElement('span');
    body.className = 'ob-category__body';
    var name = document.createElement('span');
    name.className = 'ob-category__name';
    name.textContent = this.config.strings.none;
    body.appendChild(name);

    btn.appendChild(thumb);
    btn.appendChild(body);
    btn.addEventListener('click', onClick);
    return btn;
  };

  // Screen 2: the product options, scoped to the active category when present.
  OutfitBuilder.prototype.productGrid = function (slot) {
    var self = this;
    var grid = document.createElement('div');
    grid.className = 'ob-options';

    var currentIdx = this.state.selection[slot.id];

    // Without a category step, keep the inline "None" option here as before.
    if (slot.optional && !slot.useCategories) {
      grid.appendChild(this.optionButton(null, currentIdx === null, function () {
        self.setSelection(slot.id, null);
        self.closePanels();
      }));
    }

    var indices = this.activeCategory
      ? this.activeCategory.indices
      : slot.products.map(function (_, i) { return i; });

    // Picking a product commits the choice and returns to the outfit view.
    indices.forEach(function (idx) {
      var product = slot.products[idx];
      grid.appendChild(self.optionButton(product, currentIdx === idx, function () {
        self.setSelection(slot.id, idx);
        self.closePanels();
      }));
    });

    return grid;
  };

  // The option is a role="button" card rather than a <button> so it can host a
  // swipeable image gallery with its own arrow buttons (nesting <button>s inside
  // a <button> is invalid). Tap/click or Enter/Space selects; swiping the images
  // or clicking the arrows browses without selecting.
  OutfitBuilder.prototype.optionButton = function (product, selected, onClick) {
    var el = document.createElement('div');
    el.className = 'ob-option' + (selected ? ' is-selected' : '');
    el.setAttribute('role', 'button');
    el.setAttribute('tabindex', '0');
    el.setAttribute('aria-pressed', String(selected));

    var thumb = document.createElement('span');
    thumb.className = 'ob-option__thumb';

    var images = product && product.images && product.images.length
      ? product.images
      : (product && product.thumb ? [product.thumb] : []);

    if (images.length > 1) {
      thumb.appendChild(this.imageGallery(images));
    } else if (images.length === 1) {
      var img = document.createElement('img');
      img.src = images[0];
      img.alt = '';
      img.loading = 'lazy';
      thumb.appendChild(img);
    } else {
      thumb.classList.add('ob-option__thumb--none');
      thumb.textContent = '\u2014';
    }

    var title = document.createElement('span');
    title.className = 'ob-option__title';
    title.textContent = product ? product.title : this.config.strings.none;

    el.appendChild(thumb);
    el.appendChild(title);

    if (product) {
      var price = document.createElement('span');
      price.className = 'ob-option__price';
      price.textContent = product.priceFormatted;
      if (product.compareAtFormatted) {
        var was = document.createElement('s');
        was.textContent = product.compareAtFormatted;
        price.appendChild(document.createTextNode(' '));
        price.appendChild(was);
      }
      el.appendChild(price);
      if (!product.available) {
        el.classList.add('is-soldout');
        var flag = document.createElement('span');
        flag.className = 'ob-option__flag';
        flag.textContent = this.config.strings.soldOut;
        el.appendChild(flag);
      }
    }

    el.addEventListener('click', onClick);
    el.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        onClick();
      }
    });
    return el;
  };

  // Swipeable image strip (native horizontal scroll-snap) with position dots and
  // hover arrows for pointer devices. Browsing never triggers selection.
  OutfitBuilder.prototype.imageGallery = function (images) {
    var gallery = document.createElement('span');
    gallery.className = 'ob-gallery';

    var track = document.createElement('span');
    track.className = 'ob-gallery__track';
    images.forEach(function (src) {
      var img = document.createElement('img');
      img.className = 'ob-gallery__img';
      img.src = src;
      img.alt = '';
      img.loading = 'lazy';
      img.draggable = false;
      track.appendChild(img);
    });
    gallery.appendChild(track);

    var dots = document.createElement('span');
    dots.className = 'ob-gallery__dots';
    images.forEach(function (_, i) {
      var dot = document.createElement('span');
      dot.className = 'ob-gallery__dot' + (i === 0 ? ' is-active' : '');
      dots.appendChild(dot);
    });
    gallery.appendChild(dots);

    track.addEventListener('scroll', function () {
      var idx = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      for (var i = 0; i < dots.children.length; i++) {
        dots.children[i].classList.toggle('is-active', i === idx);
      }
    }, { passive: true });

    gallery.appendChild(this.galleryArrow(track, -1));
    gallery.appendChild(this.galleryArrow(track, 1));
    return gallery;
  };

  OutfitBuilder.prototype.galleryArrow = function (track, dir) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.tabIndex = -1; // browse aid for pointer users; keep out of the tab order
    btn.className = 'ob-gallery__arrow ob-gallery__arrow--' + (dir < 0 ? 'prev' : 'next');
    btn.setAttribute('aria-label', dir < 0 ? 'Previous image' : 'Next image');
    btn.innerHTML = dir < 0 ? '&lsaquo;' : '&rsaquo;';
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation(); // don't select the product
      track.scrollBy({ left: dir * track.clientWidth, behavior: 'smooth' });
    });
    return btn;
  };

  /* ------------------------------------------------------------------ */
  /* Shop-the-look drawer                                                */
  /* ------------------------------------------------------------------ */

  OutfitBuilder.prototype.openDrawer = function () {
    var self = this;
    var s = this.config.strings;
    var drawer = this.els.drawer;
    drawer.innerHTML = '';
    drawer.appendChild(this.panelHeader(this.els.shopBtn.textContent.trim(), function () { self.closePanels(); }));

    var selected = this.slots
      .map(function (slot) { return { slot: slot, product: self.selectedProduct(slot) }; })
      .filter(function (row) { return row.product; });

    if (!selected.length) {
      var p = document.createElement('p');
      p.className = 'ob-drawer__empty';
      p.textContent = s.nothingSelected;
      drawer.appendChild(p);
      this.openPanel(drawer);
      return;
    }

    var list = document.createElement('div');
    list.className = 'ob-lines';

    selected.forEach(function (row) {
      var line = document.createElement('div');
      line.className = 'ob-line';
      if (row.slot.bg) line.style.setProperty('--ob-slot-bg', row.slot.bg);

      var thumb = document.createElement('img');
      thumb.className = 'ob-line__thumb';
      thumb.src = row.product.thumb || '';
      thumb.alt = '';

      var body = document.createElement('div');
      body.className = 'ob-line__body';

      var title = document.createElement('a');
      title.className = 'ob-line__title';
      title.href = row.product.url;
      title.textContent = row.product.title;

      var price = document.createElement('span');
      price.className = 'ob-line__price';
      price.textContent = row.product.priceFormatted;
      // Struck-through compare-at ("full") price beside the current one when the
      // item is on sale — same treatment as the picker options and the total.
      if (row.product.compareAtFormatted) {
        var was = document.createElement('s');
        was.textContent = row.product.compareAtFormatted;
        price.appendChild(document.createTextNode(' '));
        price.appendChild(was);
      }

      body.appendChild(title);
      body.appendChild(price);

      // Size pills (like a product page) instead of a dropdown. Nothing is
      // selected by default so a shopper can't accidentally buy the wrong
      // size — they must actively tap the size they want. `row.line` and
      // `row.needsSize` are stashed so validation can highlight this line.
      if (!row.product.hasOnlyDefaultVariant) {
        var sizes = document.createElement('div');
        sizes.className = 'ob-line__sizes';
        sizes.setAttribute('role', 'radiogroup');
        sizes.setAttribute('aria-label', s.size + ' — ' + row.product.title);

        var remembered = self.state.sizes[row.slot.id];
        row.product.variants.forEach(function (v) {
          var pill = document.createElement('button');
          pill.type = 'button';
          pill.className = 'ob-size';
          pill.textContent = v.title;
          pill.setAttribute('role', 'radio');
          var isSel = remembered != null && String(remembered) === String(v.id);
          pill.setAttribute('aria-checked', String(isSel));
          if (isSel) pill.classList.add('is-selected');
          if (!v.available) {
            pill.disabled = true;
            pill.classList.add('is-soldout');
            pill.setAttribute('aria-label', v.title + ' — ' + s.soldOut);
          }
          pill.addEventListener('click', function () {
            self.state.sizes[row.slot.id] = String(v.id);
            sizes.querySelectorAll('.ob-size').forEach(function (b) {
              var on = b === pill;
              b.classList.toggle('is-selected', on);
              b.setAttribute('aria-checked', String(on));
            });
            line.classList.remove('ob-line--needs-size');
            hint.hidden = true;
          });
          sizes.appendChild(pill);
        });

        var hint = document.createElement('p');
        hint.className = 'ob-line__hint';
        hint.textContent = s.selectSize;
        hint.hidden = true;

        body.appendChild(sizes);
        body.appendChild(hint);
        row.line = line;
        row.hint = hint;
        row.sizesEl = sizes;
        row.needsSize = true;
      }

      line.appendChild(thumb);
      line.appendChild(body);
      list.appendChild(line);
    });

    drawer.appendChild(list);

    var foot = document.createElement('div');
    foot.className = 'ob-drawer__foot';

    var total = document.createElement('div');
    total.className = 'ob-drawer__total';
    this.renderTotalInto(total, this.computeTotals());

    var status = document.createElement('p');
    status.className = 'ob-drawer__status';
    status.setAttribute('role', 'status');

    var addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'ob__cta ob-drawer__add';
    addBtn.textContent = s.addAll;
    addBtn.addEventListener('click', function () {
      self.addAllToCart(selected, addBtn, status);
    });

    foot.appendChild(total);
    foot.appendChild(addBtn);
    drawer.appendChild(foot);
    drawer.appendChild(status);

    this.openPanel(drawer);
  };

  OutfitBuilder.prototype.addAllToCart = function (selected, button, status) {
    var self = this;
    var s = this.config.strings;

    // Attribution properties stamped on every line so real (paid) orders can be
    // reported on later. Underscore-prefixed names are hidden from the shopper
    // in cart/checkout but persist onto the order and are readable via the
    // Admin API, order exports, and Shopify Flow. `_outfit_look` records the
    // exact combination bought, e.g. "top:black-hoodie,bottom:track-pants".
    // Every multi-variant product must have a size chosen before we add
    // anything — otherwise the shopper could buy an unintended variant.
    var missing = [];
    selected.forEach(function (row) {
      if (row.needsSize && !self.state.sizes[row.slot.id]) missing.push(row);
    });
    if (missing.length) {
      missing.forEach(function (row) {
        if (row.line) row.line.classList.add('ob-line--needs-size');
        if (row.hint) row.hint.hidden = false;
      });
      status.textContent = s.selectSize;
      var firstMissing = missing[0];
      if (firstMissing.line && firstMissing.line.scrollIntoView) {
        firstMissing.line.scrollIntoView({ block: 'nearest' });
      }
      var firstPill = firstMissing.sizesEl && firstMissing.sizesEl.querySelector('.ob-size:not(:disabled)');
      if (firstPill) firstPill.focus();
      return;
    }

    var attribution = null;
    if (this.config.trackOrders) {
      attribution = {
        _outfit_builder: '1',
        _outfit_look: selected.map(function (row) {
          return row.slot.id + ':' + row.product.handle;
        }).join(',')
      };
    }

    var items = [];
    for (var i = 0; i < selected.length; i++) {
      var row = selected[i];
      var variantId;
      if (row.product.hasOnlyDefaultVariant) {
        variantId = row.product.variants[0] && row.product.variants[0].id;
      } else {
        variantId = this.state.sizes[row.slot.id];
      }
      if (variantId) {
        var line = { id: Number(variantId), quantity: 1 };
        if (attribution) line.properties = attribution;
        items.push(line);
      }
    }
    if (!items.length) {
      status.textContent = s.nothingSelected;
      return;
    }

    button.disabled = true;
    var originalLabel = button.textContent;
    button.textContent = s.adding;
    status.textContent = '';

    fetch(this.config.routes.cartAdd + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) {
        if (!res.ok) {
          return res.json().then(function (data) {
            throw new Error(data.description || data.message || s.error);
          });
        }
        return res.json();
      })
      .then(function () {
        self.emit('outfit-builder:items-added', {
          items: items,
          look: attribution ? attribution._outfit_look : null
        });
        // Generic hook some themes/apps listen for; harmless otherwise.
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        if (self.config.afterAdd === 'cart') {
          window.location.href = self.config.routes.cart;
        } else {
          // "Stay on page": hand off to the theme's own cart. Close our drawer
          // and try to open the theme's cart drawer (refreshed with the new
          // items). If the theme has no drawer we can drive, fall back to an
          // in-panel confirmation so there's always feedback.
          button.disabled = false;
          button.textContent = originalLabel;
          if (self.openThemeCart()) {
            self.closePanels();
          } else {
            button.textContent = s.added;
            status.textContent = s.added;
            window.setTimeout(function () {
              button.textContent = originalLabel;
            }, 2000);
          }
        }
      })
      .catch(function (err) {
        status.textContent = err.message || s.error;
        button.disabled = false;
        button.textContent = originalLabel;
      });
  };

  // Open the theme's own cart drawer after a "stay on page" add, refreshed so
  // it shows the items we just added. Themes vary, so this is best-effort and
  // returns true only if it found something to open (the caller falls back to
  // an in-panel confirmation otherwise).
  OutfitBuilder.prototype.openThemeCart = function () {
    // 1) Online Store 2.0 themes (Dawn and its many derivatives) expose a
    //    <cart-drawer> custom element. renderContents() swaps in fresh section
    //    HTML and opens the drawer. Feed it the Section Rendering API output so
    //    the drawer reflects the new line items without a full page load.
    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      var url = root + '?sections=cart-drawer,cart-icon-bubble';
      fetch(url, { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (sections) {
          // Dawn's is-empty class hides the contents; drop it before rendering.
          var inner = cartDrawer.querySelector('.drawer__inner');
          if (inner) inner.classList.remove('is-empty');
          cartDrawer.classList.remove('is-empty');
          cartDrawer.renderContents({ sections: sections });
        })
        .catch(function () {
          if (typeof cartDrawer.open === 'function') cartDrawer.open();
        });
      return true;
    }

    // 2) Clean Canvas themes (Symmetry, Expanse, Vantage, …) also use a
    //    <cart-drawer> element, but it's driven by namespaced dispatch events
    //    rather than Dawn's renderContents(). Opening it alone would show a
    //    stale drawer after our AJAX add, so refresh (re-render from the server)
    //    first, then open. cartRefreshHandler() covers older versions (≈5.5.1)
    //    that predate the refresh event. The events are inert on other themes.
    if (cartDrawer) {
      if (typeof cartDrawer.cartRefreshHandler === 'function') {
        try { cartDrawer.cartRefreshHandler(); } catch (e) {}
      }
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { detail: { opener: true } }));
      return true;
    }

    // 3) Generic fallback: click a visible cart-drawer toggle / cart icon that
    //    the theme wires up to open its drawer. Skip plain links that would
    //    navigate to /cart (those don't "stay on page").
    var toggles = [
      '[data-cart-drawer-toggle]',
      '.js-drawer-open-cart',
      '#cart-icon-bubble',
      '[aria-controls="CartDrawer"]',
      '.header__icon--cart[aria-expanded]'
    ];
    for (var i = 0; i < toggles.length; i++) {
      var el = document.querySelector(toggles[i]);
      if (el) { el.click(); return true; }
    }

    return false;
  };

  /* ------------------------------------------------------------------ */
  /* Panels (shared open/close, overlay, focus, Escape)                  */
  /* ------------------------------------------------------------------ */

  OutfitBuilder.prototype.panelHeader = function (title, onClose, onBack) {
    var header = document.createElement('div');
    header.className = 'ob-panel__header';

    var heading = document.createElement('div');
    heading.className = 'ob-panel__heading';

    if (onBack) {
      var back = document.createElement('button');
      back.type = 'button';
      back.className = 'ob-panel__back';
      back.setAttribute('aria-label', this.config.strings.back);
      back.innerHTML = '&lsaquo;';
      back.addEventListener('click', onBack);
      heading.appendChild(back);
    }

    var h = document.createElement('p');
    h.className = 'ob-panel__title';
    h.textContent = title;
    heading.appendChild(h);

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'ob-panel__close';
    close.setAttribute('aria-label', this.config.strings.close);
    close.innerHTML = '&times;';
    close.addEventListener('click', onClose);

    header.appendChild(heading);
    header.appendChild(close);
    return header;
  };

  OutfitBuilder.prototype.openPanel = function (panel) {
    var other = panel === this.els.picker ? this.els.drawer : this.els.picker;
    other.hidden = true;
    this.lastFocus = document.activeElement;
    this.els.overlay.hidden = false;
    panel.hidden = false;
    this.lockScroll();
    var first = panel.querySelector(FOCUSABLE);
    if (first) first.focus();
  };

  OutfitBuilder.prototype.closePanels = function () {
    this.els.picker.hidden = true;
    this.els.drawer.hidden = true;
    this.els.overlay.hidden = true;
    this.unlockScroll();
    this.activeSlotId = null;
    if (this.lastFocus && document.contains(this.lastFocus)) this.lastFocus.focus();
  };

  // Freeze the page behind an open panel so swiping the picker/drawer can't
  // scroll the content underneath. Pad for the removed scrollbar to avoid a
  // desktop layout shift; overscroll-behavior on the panels (CSS) stops any
  // in-panel scroll from chaining out.
  OutfitBuilder.prototype.lockScroll = function () {
    if (this._scrollLocked) return;
    var doc = document.documentElement;
    var body = document.body;

    this._scrollY = window.pageYOffset || doc.scrollTop || 0;
    this._prevBody = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      width: body.style.width
    };

    // Pin the page with position:fixed. This is the reliable cross-browser lock
    // (iOS ignores overflow:hidden for touch scrolling) and, unlike a touchmove
    // guard, it leaves the panel's own scrolling completely untouched — so the
    // picker scrolls on the first swipe. Offset by the scroll position so the
    // view doesn't jump; fixing the width to the current content width keeps
    // desktop content from shifting when the scrollbar disappears.
    body.style.position = 'fixed';
    body.style.top = -this._scrollY + 'px';
    body.style.left = '0';
    body.style.width = doc.clientWidth + 'px';

    this._scrollLocked = true;
  };

  OutfitBuilder.prototype.unlockScroll = function () {
    if (!this._scrollLocked) return;
    var body = document.body;
    var prev = this._prevBody || {};
    body.style.position = prev.position || '';
    body.style.top = prev.top || '';
    body.style.left = prev.left || '';
    body.style.width = prev.width || '';
    window.scrollTo(0, this._scrollY || 0);
    this._scrollLocked = false;
  };

  /* ------------------------------------------------------------------ */
  /* Totals & money formatting                                           */
  /* ------------------------------------------------------------------ */

  // Sum the current selection. Returns the current total, the "before sale"
  // total (each item's compare-at price when it's on sale, otherwise its normal
  // price), and whether anything is actually discounted.
  OutfitBuilder.prototype.computeTotals = function () {
    var self = this;
    var cents = 0;
    var wasCents = 0;
    var any = false;
    var onSale = false;
    this.slots.forEach(function (slot) {
      var p = self.selectedProduct(slot);
      if (!p) return;
      any = true;
      cents += p.price;
      if (p.compareAt && p.compareAt > p.price) {
        wasCents += p.compareAt;
        onSale = true;
      } else {
        wasCents += p.price;
      }
    });
    return { any: any, onSale: onSale, cents: cents, wasCents: wasCents };
  };

  // Fill an element with the current total, prefixed by a struck-through
  // "before sale" total when the selection includes discounted items \u2014 mirrors
  // how individual picker options show price vs. compare-at.
  OutfitBuilder.prototype.renderTotalInto = function (el, totals) {
    el.textContent = '';
    if (!totals.any) { el.textContent = '\u2014'; return; }
    if (totals.onSale) {
      var was = document.createElement('s');
      was.className = 'ob__total-was';
      was.textContent = this.formatMoney(totals.wasCents);
      el.appendChild(was);
      el.appendChild(document.createTextNode(' '));
    }
    el.appendChild(document.createTextNode(this.formatMoney(totals.cents)));
  };

  OutfitBuilder.prototype.updateTotal = function () {
    this.renderTotalInto(this.els.total, this.computeTotals());
  };

  OutfitBuilder.prototype.formatMoney = function (cents) {
    var value = cents / 100;
    var two = value.toFixed(2);
    var noDec = String(Math.round(value));
    var comma = two.replace('.', ',');
    var fmt = this.config.moneyFormat || '${{amount}}';
    var formatted = fmt
      .replace(/\{\{\s*amount\s*\}\}/g, two)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, comma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec);
    return stripHtml(formatted);
  };

  /* ------------------------------------------------------------------ */
  /* Wiring                                                              */
  /* ------------------------------------------------------------------ */

  OutfitBuilder.prototype.bind = function () {
    var self = this;

    this.els.viewBtns.forEach(function (btn) {
      btn.addEventListener('click', function () { self.setView(btn.dataset.obView); });
    });

    this.els.shopBtn.addEventListener('click', function () { self.openDrawer(); });
    if (this.els.resetBtn) {
      this.els.resetBtn.addEventListener('click', function () { self.reset(); });
    }
    this.els.overlay.addEventListener('click', function () { self.closePanels(); });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') self.closePanels();
    });

    // Re-fit the canvas when the viewport changes (rotate, resize, toolbar show/hide).
    var scheduled = false;
    this._onResize = function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () { scheduled = false; self.fitCanvas(); });
    };
    window.addEventListener('resize', this._onResize);
    window.addEventListener('orientationchange', this._onResize);
  };

  // On mobile the stage is a flex box that owns whatever height is left after the
  // heading, rail and buy bar. Size the canvas to contain-fit that box so the
  // whole outfit shows without overflowing the width or forcing a page scroll.
  // On desktop the canvas is sized by CSS, so clear any inline sizing.
  OutfitBuilder.prototype.fitCanvas = function () {
    var canvas = this.els.canvas;
    if (!canvas) return;
    var stage = canvas.parentNode;

    if (!window.matchMedia('(max-width: 899px)').matches) {
      canvas.style.width = '';
      canvas.style.height = '';
      return;
    }

    var aspect = parseFloat(getComputedStyle(this.root).getPropertyValue('--ob-aspect-num')) || 0.6667;
    var availW = stage.clientWidth;
    var availH = stage.clientHeight;
    if (availW <= 0 || availH <= 0) return;

    var width = availW;
    var height = width / aspect;
    if (height > availH) {
      height = availH;
      width = height * aspect;
    }
    canvas.style.width = Math.floor(width) + 'px';
    canvas.style.height = Math.floor(height) + 'px';
  };

  /* ------------------------------------------------------------------ */
  /* Boot                                                                */
  /* ------------------------------------------------------------------ */

  function init() {
    document.querySelectorAll('[data-outfit-builder]').forEach(function (el) {
      if (!el.outfitBuilder) el.outfitBuilder = new OutfitBuilder(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when the merchant edits the section in the theme editor.
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('[data-outfit-builder]');
    if (el) el.outfitBuilder = new OutfitBuilder(el);
  });
})();
